import { InputNumber, InputNumberProps, InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { classNames } from 'primereact/utils';
import {
  FieldValues,
  useController,
  UseControllerProps,
  UseFormClearErrors,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ErrorMessage from '../../error-message';
import styles from './NumberInput.module.css';

export type NumberInputProps<T extends FieldValues> = Omit<InputNumberProps, 'name'> & {
  label?: string;
  clearErrors?: UseFormClearErrors<T>;
  showError?: boolean;
  customIcon?: React.ReactNode;
  customIconPosition?: 'left' | 'right';
} & UseControllerProps<T>;

const NumberInput = <T extends FieldValues>({
  name,
  control,
  rules,
  defaultValue,
  label,
  className,
  clearErrors,
  showError = true,
  customIcon,
  customIconPosition = 'right',
  ...props
}: NumberInputProps<T>) => {
  const {
    field: { value, ref, onChange, ...inputProps },
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
  });

  const {
    i18n: { language },
  } = useTranslation();

  const onValueChange = (event: InputNumberValueChangeEvent) => {
    onChange(event);
    clearErrors?.(name);
  };

  return (
    <div className='field p-fluid'>
      <span className={classNames(`p-input-icon-${customIconPosition}`, 'p-float-label')}>
        <InputNumber
          inputId={name}
          inputRef={ref}
          value={value ?? null}
          onValueChange={onValueChange}
          locale={language}
          {...props}
          {...inputProps}
          className={classNames(className, {
            'p-invalid': error,
          })}
        />
        {customIcon && <>{customIcon}</>}
        {label && <label htmlFor={name}>{label}</label>}
      </span>
      {showError && invalid && (
        <div className={classNames(styles['error-message-box'])}>
          <ErrorMessage message={error?.message || ''} />
        </div>
      )}
    </div>
  );
};

export default NumberInput;
